import { MatError } from "@angular/material/form-field";
import { TranslateModule } from "@ngx-translate/core";
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  ViewChild,
  ViewEncapsulation,
  forwardRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from "@angular/core";

import { ButtonComponent } from "../button/button.component";
import { ValidationHandlerPipe } from "../../pipes";
import { ReactiveFormsBaseComponent } from "../../base-components";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "app-input-count",
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, ButtonComponent, MatError, ValidationHandlerPipe],
  templateUrl: "./input-count.component.html",
  styleUrl: "./input-count.component.scss",
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputCountComponent),
      multi: true,
    },
  ],
})
export class InputCountComponent extends ReactiveFormsBaseComponent {
  readonly destroyRef = inject(DestroyRef);

  override readonly formControlName = input("");
  readonly count = input(0);
  readonly label = input<string>();
  readonly readonly = input(false);
  readonly withInput = input(true);
  readonly disablePlus = input(false);
  readonly disableMinus = input(false);
  readonly displayAsRow = input(false);

  @ViewChild("mirrorSpan") mirrorSpan!: ElementRef;

  countValue = linkedSignal(this.count || signal(this.value || 0));

  readonly inputValue = output<number>();

  get min(): number {
    const validatorFn = this.control?.validator;
    if (!validatorFn) return 0;

    // Create a fake control with a value guaranteed to be too small
    const result = validatorFn({ value: Number.NEGATIVE_INFINITY } as any);
    const minValue = result?.["min"]?.min;

    if (minValue !== undefined) {
      return minValue;
    }

    return 0;
  }

  get max() {
    if (this.control?.validator?.(this.control)) return this.control?.validator(this.control)?.["max"]?.max || 1000000;

    return 1000000;
  }

  get disabled() {
    return this.readonly() || this.control?.disabled;
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.

    this.control?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => this.countValue.set(value || 0));
  }

  plus() {
    if (this.disabled) return;
    if (this.countValue() >= this.max) return;
    if (!this.value && this.formControlName()) this.countValue.set(this.value || 0);

    this.countValue.update((value) => value + 1);
    this.control?.markAsDirty();
    this.control?.setValue(this.countValue());
    this.inputValue.emit(this.countValue());
  }

  minus() {
    if (this.disabled) return;
    if (this.countValue() <= this.min) return;

    if (this.countValue() > this.min) {
      this.countValue.update((value) => value - 1);
      this.control?.markAsDirty();
      this.control?.setValue(this.countValue());
      this.inputValue.emit(this.countValue());
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    this.control?.markAsDirty();
    const preventedCharacters = ["e", "E", "+", "."];
    this.min >= 0 ? preventedCharacters.push("-") : null;
    if (preventedCharacters.includes(event.key)) {
      event.preventDefault();
    }
  }

  @HostListener("paste", ["$event"]) onPaste(event: ClipboardEvent): void {
    if (event.clipboardData?.getData("text/plain").toLowerCase().includes("e")) {
      event.preventDefault();
    }
  }

  resizeInput(input: HTMLInputElement) {
    const mirror = this.mirrorSpan.nativeElement;
    mirror.textContent = input.value || input.placeholder || " ";
    input.style.width = mirror.offsetWidth + 32 + "px";
  }
}
